import { useState } from 'react'
import { useAuthActions } from '@convex-dev/auth/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AuthErrorHandling, logError } from '@/utils/errorHandling'
// Frontend validation functions (matching backend schema)
const validateUserLogin = (data: { email: string; password: string }) => {
  const errors: string[] = []

  if (!data.email || data.email.length === 0) {
    errors.push('Email is required')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Invalid email format')
  } else if (data.email.length > 255) {
    errors.push('Email too long')
  }

  if (!data.password || data.password.length === 0) {
    errors.push('Password is required')
  } else if (data.password.length < 6) {
    errors.push('Password must be at least 6 characters')
  } else if (data.password.length > 100) {
    errors.push('Password too long')
  }

  if (errors.length > 0) {
    const error = new Error(errors[0])
    ;(error as any).errors = errors.map((msg, i) => ({
      path: i === 0 && msg.includes('Email') ? ['email'] : ['password'],
      message: msg,
    }))
    throw error
  }

  return data
}

const validateUserRegister = (data: {
  name: string
  email: string
  password: string
  confirmPassword: string
}) => {
  const errors: Record<string, string> = {}

  if (!data.name || data.name.length === 0) {
    errors.name = 'Name is required'
  } else if (data.name.length > 100) {
    errors.name = 'Name too long'
  }

  if (!data.email || data.email.length === 0) {
    errors.email = 'Email is required'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Invalid email format'
  } else if (data.email.length > 255) {
    errors.email = 'Email too long'
  }

  if (!data.password || data.password.length === 0) {
    errors.password = 'Password is required'
  } else if (data.password.length < 6) {
    errors.password = 'Password must be at least 6 characters'
  } else if (data.password.length > 100) {
    errors.password = 'Password too long'
  }

  if (!data.confirmPassword || data.confirmPassword.length === 0) {
    errors.confirmPassword = 'Password confirmation required'
  } else if (data.password !== data.confirmPassword) {
    errors.confirmPassword = "Passwords don't match"
  }

  if (Object.keys(errors).length > 0) {
    const error = new Error('Validation failed')
    ;(error as any).errors = Object.entries(errors).map(([path, message]) => ({
      path: [path],
      message,
    }))
    throw error
  }

  return data
}

export function LoginPage() {
  const { signIn } = useAuthActions()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isRegisterMode = searchParams.get('mode') === 'register'

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Clear field error when user starts typing
  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    setFieldErrors({})

    // Validate using Zod schemas
    try {
      if (isRegisterMode) {
        validateUserRegister({
          name,
          email,
          password,
          confirmPassword,
        })
        // If validation passes, proceed with registration
      } else {
        validateUserLogin({
          email,
          password,
        })
        // If validation passes, proceed with login
      }
    } catch (validationError: any) {
      setIsLoading(false)
      // Handle Zod validation errors
      if (validationError.errors) {
        const errors: Record<string, string> = {}
        validationError.errors.forEach((err: any) => {
          errors[err.path[0]] = err.message
        })
        setFieldErrors(errors)
        return
      } else {
        setError(validationError.message || 'Validation failed')
        return
      }
    }

    // Retry logic for transient connection errors
    const MAX_RETRIES = 2
    let retryCount = 0
    let lastError: any = null

    while (retryCount <= MAX_RETRIES) {
      try {
        if (retryCount > 0) {
          // Show reconnecting message on retry
          toast.loading(
            `Reconnecting... (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`,
            {
              id: 'auth-retry',
            }
          )
          // Brief delay before retry (exponential backoff)
          await new Promise(resolve =>
            setTimeout(
              resolve,
              Math.min(1000 * Math.pow(2, retryCount - 1), 3000)
            )
          )
        }

        if (isRegisterMode) {
          await signIn('password', {
            email,
            password,
            name: name || email.split('@')[0],
            flow: 'signUp',
          })
          toast.success('Account created successfully! Welcome!')
        } else {
          await signIn('password', { email, password, flow: 'signIn' })
          toast.success('Welcome back!')
        }

        // Dismiss retry toast if it exists
        if (retryCount > 0) {
          toast.dismiss('auth-retry')
        }

        // Navigate to home after successful authentication
        navigate('/', { replace: true })
        return // Success - exit function
      } catch (err) {
        lastError = err
        const errorString = String(err)

        // Check if error is a transient connection issue
        const isConnectionError =
          errorString.includes('Connection lost') ||
          errorString.includes('Network') ||
          errorString.includes('timeout') ||
          errorString.includes('ECONNREFUSED')

        // Log the error
        logError(
          err,
          `Authentication - ${isRegisterMode ? 'sign up' : 'sign in'} (attempt ${retryCount + 1})`
        )

        // If it's a connection error and we have retries left, continue loop
        if (isConnectionError && retryCount < MAX_RETRIES) {
          retryCount++
          continue
        }

        // Not a connection error or out of retries - break and show error
        break
      }
    }

    // If we get here, all retries failed
    toast.dismiss('auth-retry')
    setIsLoading(false)

    // Use centralized error handling for final error
    const errorMessage = isRegisterMode
      ? AuthErrorHandling.signUp(lastError)
      : AuthErrorHandling.signIn(lastError)

    setError(errorMessage)
    toast.error(errorMessage)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-xl font-semibold text-foreground">
            {isRegisterMode ? 'Create your account' : 'Sign in to your account'}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {isRegisterMode ? 'Or ' : 'Or '}
            <Link
              to={isRegisterMode ? '/auth/login' : '/auth/login?mode=register'}
              className="font-semibold text-primary hover:text-primary/80"
            >
              {isRegisterMode
                ? 'sign in to your existing account'
                : 'create a new account'}
            </Link>
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {isRegisterMode ? 'Welcome to VRP System' : 'Welcome back'}
            </CardTitle>
            <CardDescription>
              {isRegisterMode
                ? 'Create your account to get started with vehicle routing optimization'
                : 'Enter your credentials to access your account'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegisterMode && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={e => {
                      setName(e.target.value)
                      clearFieldError('name')
                    }}
                    placeholder="Enter your full name"
                    disabled={isLoading}
                    autoComplete="name"
                    className={fieldErrors.name ? 'border-red-500' : ''}
                  />
                  {fieldErrors.name && (
                    <p className="text-sm text-red-600">{fieldErrors.name}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value)
                    clearFieldError('email')
                  }}
                  placeholder="Enter your email"
                  required
                  disabled={isLoading}
                  autoComplete="email"
                  className={fieldErrors.email ? 'border-red-500' : ''}
                />
                {fieldErrors.email && (
                  <p className="text-sm text-red-600">{fieldErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value)
                    clearFieldError('password')
                  }}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                  minLength={isRegisterMode ? 6 : undefined}
                  autoComplete={
                    isRegisterMode ? 'new-password' : 'current-password'
                  }
                  className={fieldErrors.password ? 'border-red-500' : ''}
                />
                {fieldErrors.password && (
                  <p className="text-sm text-red-600">{fieldErrors.password}</p>
                )}
                {isRegisterMode && !fieldErrors.password && (
                  <p className="text-sm text-muted-foreground">
                    Password must be at least 6 characters
                  </p>
                )}
              </div>

              {isRegisterMode && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={e => {
                      setConfirmPassword(e.target.value)
                      clearFieldError('confirmPassword')
                    }}
                    placeholder="Confirm your password"
                    required
                    disabled={isLoading}
                    minLength={6}
                    autoComplete="new-password"
                    className={
                      fieldErrors.confirmPassword ? 'border-red-500' : ''
                    }
                  />
                  {fieldErrors.confirmPassword && (
                    <p className="text-sm text-red-600">
                      {fieldErrors.confirmPassword}
                    </p>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading
                  ? isRegisterMode
                    ? 'Creating account...'
                    : 'Signing in...'
                  : isRegisterMode
                    ? 'Create Account'
                    : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default LoginPage
